import { Dayjs } from 'dayjs'

export enum HealthDataType {
  REPORT = 'report',
  CAT_LAB_RESULT = 'categoricalLabResult',
  DRUG_EVENT = 'drugEvent',
  HEALTH_CONSTANT = 'healthConstant',
  LAB_RESULT = 'labResult',
  TEXTUAL_HEALTH_ENTRY = 'textualHealthEntry',
  ANNOTATION = 'annotation',
}

export enum TextualHealthEntryType {
  MEDICAL_HISTORY = 'medicalHistory',
  NURSE_REPORT = 'nurseReport',
  LAB_RESULT_COMMENT = 'labResultComment',
  TARGET_NURSE_REPORT = 'targetNurseReport',
  SURGERY_LABEL = 'surgeryLabel',
}

export enum DrugEventType {
  drugAdministration = 'Administration',
  drugPrescription = 'Prescription',
}

export enum DrugEventPrestationType {
  NONE = 'null',
  LIST_EXTRA = '06',
  MED_APAC = '9',
  ANTICANCER = '14',
}

export enum DrugEventPrestationTypeLabel {
  NONE = 'Médicaments',
  LIST_EXTRA = 'Liste en sus',
  MED_APAC = 'MED AP-AC',
  ANTICANCER = 'Anticancéreux intra-GHS',
}

export interface HealthData<TDate> {
  id: number
  creationDate: TDate
}

export interface BaseChunk {
  string: string
  chunkStart: number
  chunkEnd: number
  hash: string
  isDoubt: boolean
  isHighlighted: boolean
  isNegative: boolean
}

type MatchOrigins = {
  savedSearchIds?: number[]
  predictions?: boolean[]
  cohortNodeIds?: number[]
}

interface Drug {
  shortUcd: number | null
  commercialName: string | null
  atc: string | null
  atcLabel: string | null
  activeSubstance: string | null
}

export interface BaseDrugEvent<TDate> extends HealthData<TDate> {
  id: number
  amount: number | null
  drug: Drug
  type: DrugEventType
  unit: string | null
  prestationType: DrugEventPrestationType | null
  prescriptionValidation: boolean | null
  indication: string | null
  matchOrigins?: MatchOrigins
}

export type HealthDataDocument = {
  type: string
  targetId: number | null
  drugEventPrestationType?: DrugEventPrestationType | null
}

export type RawDrugEvent = BaseDrugEvent<string>

export type DrugEvent = BaseDrugEvent<Dayjs>

export interface DocumentSourceIdentifier {
  id: number
  type: HealthDataType
}

export type Annotation = {
  id: number
  namedEntityConfigId: string
  namedEntityName: string
  value: string
  unit: string
  documentSourceIdentifier: DocumentSourceIdentifier | null
}

export interface HealthConstantType {
  description: string
}

export interface BaseHealthConstant<TDate> extends HealthData<TDate> {
  constantType: HealthConstantType
  value: number | null
  unit?: string | null
  matchOrigins?: MatchOrigins
  documentSourceIdentifier: DocumentSourceIdentifier | null
}

export type RawHealthConstant = BaseHealthConstant<string>

export type HealthConstant = BaseHealthConstant<Dayjs>

export interface BaseLabResult<TDate> extends HealthData<TDate> {
  code: string | null
  conclusion: string | null
  title: string | null
  value: string | number | null
  unit?: string | null
  matchOrigins?: MatchOrigins
  documentSourceIdentifier: DocumentSourceIdentifier | null
}

export type RawLabResult = BaseLabResult<string>

export type LabResult = BaseLabResult<Dayjs>

export interface BaseNumericalLabResult<TDate> extends BaseLabResult<TDate> {
  value: number | null
  unit: string
}

export type RawNumericalLabResult = BaseNumericalLabResult<string>

export type NumericalLabResult = BaseNumericalLabResult<Dayjs>

export interface BaseCategoricalLabResult<TDate> extends BaseLabResult<TDate> {
  value: string | null
}

export type RawCategoricalLabResult = BaseCategoricalLabResult<string>

export type CategoricalLabResult = BaseCategoricalLabResult<Dayjs>

export interface BaseTextualHealthEntry<TChunk> extends HealthData<Date> {
  content: string
  markedAntecedentIndexSets: Array<Array<number>>
  markedFamilyAntecedentIndexSets: Array<Array<number>>
  markedDoubtIndexSets: Array<Array<number>>
  markedNegationIndexSets: Array<Array<number>>
  type: string
  chunks?: TChunk[]
}
